import { z } from 'zod';
import { SkillTriggerModeSchema } from './skill.js';
// `KgQueryModeSchema` is defined in ./task.js — reused here to avoid duplicate
// export from the barrel.
import { KgQueryModeSchema } from './task.js';
/**
 * v2 Knowledge Workspaces — cross-package contracts (Phase 1.1).
 *
 * Presentation-layer types for the "Workspace → Documents/Skills/KG" UX
 * shipped in the mockup (`docs/proposals/documents-skills-kg-proposal-v2.md`).
 * Backed by existing metaphysics DocumentSet infrastructure at the DB layer;
 * this file is the wire contract between cortex-api and console.
 *
 * Naming: a `Workspace` IS a metaphysics `DocumentSet` today — the rename lives
 * at the API/UI boundary. Same UUID identifies both. Phase 5 migration
 * consolidates the Pipeline path into this shape; until then those workspaces
 * are `sourceKind='user'` (uploads only).
 */
// ─── enums ────────────────────────────────────────────────────────────────────
/**
 * Where the content in a workspace comes from.
 *
 * - `user` — files uploaded by a human via the console. Today: DocumentSet.
 * - `agent` — outputs produced by a scheduled Skill Runner (Phase 5 legacy
 *   pipelines land here).
 * - `webhook` — content pulled by a `github-sync`-style connector.
 */
export const WorkspaceSourceKindSchema = z.enum(['user', 'agent', 'webhook']);
/** Per-file status pill vocabulary (Cursor's from the merged v2 §7.2). */
export const ContentItemStatusSchema = z.enum([
    'raw',
    'needs-skill',
    'processing',
    'ready',
    'failed',
]);
/**
 * Whether a `ContentItem` was uploaded by the user or produced by a Skill Run.
 * Sources and artifacts co-exist in the same folder tree — the field is the
 * lineage discriminator.
 */
export const ContentItemKindSchema = z.enum(['source', 'artifact']);
/** Fine-grained artifact role — surfaced in the UI as a chip next to filename. */
export const ArtifactRoleSchema = z.enum([
    'markdown',
    'summary',
    'transcript',
    'caption',
    'synthesis',
    'section',
    'index',
    'glossary',
]);
export const SkillRunStatusSchema = z.enum(['pending', 'running', 'completed', 'failed']);
export const KgVersionStatusSchema = z.enum(['building', 'ready', 'archived', 'failed']);
export const KgSyncModeSchema = z.enum(['full', 'delta']);
export const ContentVersionStatusSchema = z.enum(['DRAFT', 'COMMITTED', 'ARCHIVED']);
// ─── Workspace ────────────────────────────────────────────────────────────────
/**
 * A user-facing container for a corpus + its KG. Presentation of a metaphysics
 * DocumentSet — same UUID.
 */
export const WorkspaceSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    sourceKind: WorkspaceSourceKindSchema,
    createdBySubject: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    /**
     * Currently-active KG version (integer). Null when no KG has been built or
     * all versions are archived.
     */
    activeKgVersion: z.number().int().positive().nullable(),
    /**
     * Recompute KG on every content-version commit. Off by default; users opt in
     * on the workspace's KG-tab settings.
     */
    rebuildOnCommit: z.boolean(),
});
export const CreateWorkspaceInputSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().max(2_000).optional(),
    sourceKind: WorkspaceSourceKindSchema.optional().default('user'),
});
// ─── ContentVersion (rebranded DocumentSetVersion) ────────────────────────────
export const ContentVersionSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    status: ContentVersionStatusSchema,
    /** Null while DRAFT; required + unique per-workspace once COMMITTED. */
    name: z.string().nullable(),
    description: z.string().nullable(),
    parentVersionId: z.string().nullable(),
    createdBySubject: z.string().nullable(),
    createdAt: z.date(),
    committedAt: z.date().nullable(),
});
export const CommitContentVersionInputSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().max(2_000).optional(),
});
// ─── ContentItem (rebranded DocumentSetEntry with lineage) ────────────────────
/**
 * One file inside a workspace's content version. Sources and artifacts are
 * peers — same shape, different `kind` field.
 */
export const ContentItemSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    versionId: z.string(),
    /** POSIX path within the workspace (e.g. `/procedures/lockout.pdf`). */
    path: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    contentType: z.string().nullable(),
    sha256: z.string(),
    kind: ContentItemKindSchema,
    status: ContentItemStatusSchema,
    role: ArtifactRoleSchema.nullable(),
    /** Ids of ContentItems this artifact was produced from (empty for sources). */
    producedFromItemIds: z.array(z.string()),
    /** Id of the SkillRun that produced this item (null for sources + passthrough). */
    producedBySkillRunId: z.string().nullable(),
    createdAt: z.date(),
});
// ─── SkillAttachment (workspace ↔ skill binding) ──────────────────────────────
/**
 * Binds an org-global Skill to a specific workspace. Optional per-attachment
 * override of the skill's default trigger. The scope of "which files this
 * skill applies to" comes from the Skill.mimeMatches, not the attachment.
 */
export const SkillAttachmentSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    workspaceId: z.string(),
    skillId: z.string(),
    /** Overrides Skill.triggerMode when non-null. */
    triggerOverride: SkillTriggerModeSchema.nullable(),
    /** Optional narrower glob than Skill.mimeMatches. Not enforced today. */
    pathGlob: z.string().nullable(),
    createdAt: z.date(),
});
export const CreateSkillAttachmentInputSchema = z.object({
    workspaceId: z.string(),
    skillId: z.string(),
    triggerOverride: SkillTriggerModeSchema.optional(),
    pathGlob: z.string().max(255).optional(),
});
/**
 * Update an existing attachment's per-workspace trigger override. `null` clears
 * the override so the attachment inherits the skill's default trigger again.
 */
export const UpdateSkillAttachmentInputSchema = z.object({
    triggerOverride: SkillTriggerModeSchema.nullable(),
});
// ─── SkillRun (rebranded ProcessingRun for workspace-side runs) ───────────────
/**
 * One execution of a skill against one or more workspace items. Records
 * input/output items, status, cost/tokens. Legacy pipeline `ProcessingRun`
 * rows migrate to this table in Phase 5.
 */
export const SkillRunSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    workspaceId: z.string(),
    skillId: z.string(),
    skillVersionId: z.string().nullable(),
    inputItemIds: z.array(z.string()),
    outputItemIds: z.array(z.string()),
    status: SkillRunStatusSchema,
    progressPercent: z.number().int().min(0).max(100),
    message: z.string().nullable(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    startedAt: z.date(),
    completedAt: z.date().nullable(),
    warnings: z.array(z.string()),
});
// ─── KgVersion (rebranded DocumentSetKgVersion) ───────────────────────────────
export const KgVersionSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    /** Monotonic per workspace. */
    version: z.number().int().positive(),
    status: KgVersionStatusSchema,
    syncMode: KgSyncModeSchema,
    parentVersion: z.number().int().positive().nullable(),
    /**
     * Fingerprint of extraction schema + converter versions + LLM role config +
     * item manifest. Two versions with the same fingerprint are byte-identical
     * for KG purposes; a delta against the same fingerprint can safely no-op.
     */
    fingerprint: z.string(),
    /** LightRAG workspace name (internal — kept for backend-adapter portability). */
    kgWorkspace: z.string(),
    changesSummary: z.string(),
    entities: z.number().int().nonnegative(),
    relations: z.number().int().nonnegative(),
    chunks: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    durationSec: z.number().int().nonnegative(),
    builtBy: z.string().nullable(),
    builtAt: z.date(),
});
// ─── KG query ─────────────────────────────────────────────────────────────────
export const KgQueryInputSchema = z.object({
    query: z.string().min(1).max(4_000),
    mode: KgQueryModeSchema.default('mix'),
    /** Pin to a specific KG version; defaults to the workspace's active version. */
    kgVersion: z.number().int().positive().optional(),
    includeChunks: z.boolean().optional().default(false),
    topK: z.number().int().positive().max(50).optional(),
});
export const KgQueryResultSchema = z.object({
    answer: z.string(),
    citations: z.array(z.string()),
    workspace: z.string(),
    kgVersion: z.number().int().positive().nullable(),
    usage: z
        .object({
        tokensIn: z.number().int().nonnegative(),
        tokensOut: z.number().int().nonnegative(),
        costUsd: z.number().nonnegative(),
    })
        .nullable()
        .optional(),
});
// ─── Composite read shapes ────────────────────────────────────────────────────
/**
 * Full workspace shape for the detail page. Batches the reads the mockup did
 * separately — keeps chatty concerns to one round-trip.
 */
export const WorkspaceDetailSchema = WorkspaceSchema.extend({
    currentVersion: ContentVersionSchema,
    itemCounts: z.object({
        sources: z.number().int().nonnegative(),
        artifacts: z.number().int().nonnegative(),
        processing: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    }),
    attachedSkillCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=workspace.js.map